import type { DocumentExtractionV2Invocation } from '@lifeomic/react-client';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import type { ServiceAuth } from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';
import {
  isTaskForSource,
  selectSourceTask,
} from './documentExtractionV2Source';
import { isDocumentExtractionV2InFlight } from './DocumentExtractionV2Tasks';
import {
  DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE,
  FHIR_SOURCE_TYPE_TAG_SYSTEM,
} from './memberDocumentSource';

const documentReferenceFhirService = new FhirService('DocumentReference');

/**
 * Upper bound on summary DocumentReferences fetched per member when resolving
 * durable extraction state. The member documents table pages at 200 rows, so a
 * single page can never reference more sources than this.
 */
export const DOCUMENT_EXTRACTION_SUMMARY_SEARCH_COUNT = 1000;

export const getDocumentExtractionTask = (
  tasks: DocumentExtractionV2Invocation[],
  documentReferenceId: string,
) => {
  const sourceTasks = tasks.filter(task =>
    isTaskForSource(task, 'DocumentReference', documentReferenceId),
  );

  return (
    sourceTasks.find(
      task =>
        isDocumentExtractionV2InFlight(task) && task.status === 'processing',
    ) ??
    sourceTasks.find(isDocumentExtractionV2InFlight) ??
    selectSourceTask(tasks, 'DocumentReference', documentReferenceId)
  );
};

/**
 * Source DocumentReference ids a Document Extraction V2 summary was derived
 * from. The summary DocumentReference records each source it transformed via
 * `relatesTo[].target.reference` (e.g. `DocumentReference/<id>`), which is the
 * durable, task-independent link we use to mark a source document as extracted.
 */
export const getSummarySourceDocumentReferenceIds = (
  summary: fhir.DocumentReference,
): string[] =>
  (summary.relatesTo ?? [])
    .map(relation => relation.target?.reference)
    .filter(
      (reference): reference is string =>
        !!reference && reference.startsWith('DocumentReference/'),
    )
    .map(reference => reference.replace(/^DocumentReference\//, ''));

/**
 * Resolve which of `documentReferenceIds` have a persisted Document Extraction
 * V2 summary by reading the member's summary DocumentReferences directly.
 *
 * This intentionally does NOT reconstruct deterministic artifact ids (the
 * backend now suffixes the artifact identity with a non-reconstructable
 * `extraction_key`) and does NOT depend on the async task record (tasks expire
 * out of the visible task list). The summary DocumentReference, written for
 * every completed extraction, is the authoritative durable signal.
 */
export const loadDurableExtractedDocumentIds = async ({
  accountId,
  documentReferenceIds,
  memberId,
  projectId,
  serviceAuth,
}: {
  accountId: string;
  documentReferenceIds: string[];
  memberId: string;
  projectId: string;
  serviceAuth: ServiceAuth;
}) => {
  if (!documentReferenceIds.length) {
    return [];
  }

  const requestedDocumentReferenceIds = new Set(documentReferenceIds);

  try {
    const response = await documentReferenceFhirService.search(serviceAuth)(
      accountId,
      projectId,
      {
        subject: `Patient/${memberId}`,
        _tag: [
          `${FHIR_SOURCE_TYPE_TAG_SYSTEM}|${DOCUMENT_EXTRACTION_V2_SUMMARY_SOURCE_TYPE}`,
        ],
        _count: DOCUMENT_EXTRACTION_SUMMARY_SEARCH_COUNT,
      },
    );

    const extractedDocumentIds = new Set<string>();
    for (const entry of response.data.entry ?? []) {
      if (entry.resource?.resourceType !== 'DocumentReference') {
        continue;
      }
      for (const sourceDocumentReferenceId of getSummarySourceDocumentReferenceIds(
        entry.resource,
      )) {
        if (requestedDocumentReferenceIds.has(sourceDocumentReferenceId)) {
          extractedDocumentIds.add(sourceDocumentReferenceId);
        }
      }
    }

    return Array.from(extractedDocumentIds);
  } catch {
    return [];
  }
};
